/**
 * Typed client methods for the BEAM tasks + work-queue surface (WO-P1).
 *
 * Routes (beam-backend calendarRouter.ts / tasksRouter.ts):
 *   GET    /api/tasks            ?status&task_type&assigned_to&ranked → TaskDto[]
 *   GET    /api/tasks/:id                                             → TaskDto
 *   POST   /api/tasks           { CreateTaskRequest }                 → TaskDto
 *   PATCH  /api/tasks/:id       { UpdateTaskRequest }                 → TaskDto
 *   POST   /api/tasks/:id/complete                                    → TaskDto
 *   GET    /api/work-queue                                            → WorkQueueEntry[]
 *
 * CASING: snake_case — raw Prisma rows (see calendar.ts note). Responses parsed with
 * the zod schemas. Bearer token via src/api/client.ts. No PII in URLs.
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { get, post, patch } from './client';
import { TaskDto, WorkQueueEntry } from '@beam/api-types';
import type {
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskStatus,
  TaskType,
} from '@beam/api-types';

export interface ListTasksParams {
  status?: TaskStatus;
  task_type?: TaskType;
  assigned_to?: string;
  /** When true, the server returns tasks in ranked (priority/due) order. */
  ranked?: boolean;
}

/** Fetch a single task. */
export async function getTask(id: string): Promise<TaskDto> {
  const raw = await get<unknown>(`/api/tasks/${id}`);
  return TaskDto.parse(raw);
}

/** Create a task. */
export async function createTask(body: CreateTaskRequest): Promise<TaskDto> {
  const raw = await post<unknown>('/api/tasks', body);
  return TaskDto.parse(raw);
}

/** Patch a task (status / priority / due / reassign, etc.). */
export async function updateTask(
  id: string,
  body: UpdateTaskRequest,
): Promise<TaskDto> {
  const raw = await patch<unknown>(`/api/tasks/${id}`, body);
  return TaskDto.parse(raw);
}

/** Mark a task complete. */
export async function completeTask(id: string): Promise<TaskDto> {
  const raw = await post<unknown>(`/api/tasks/${id}/complete`);
  return TaskDto.parse(raw);
}

/** The ranked priority work queue (tasks + punchlist items + schedule events). */
export async function getWorkQueue(): Promise<WorkQueueEntry[]> {
  const raw = await get<unknown>('/api/work-queue');
  return (raw as unknown[]).map((item) => WorkQueueEntry.parse(item));
}
